/**
 * T-306 — bloc « Prospection commerciale » de la fiche client : l'état de l'opposition (visible), l'enregistrer ou la lever, et le journal.
 * Charte : tokens var(--oh-*) uniquement (clair + foncé), phrases en sentence case, boutons secondaires (le primaire de l'écran reste
 * celui de la fiche). La vérité est au SERVEUR : l'état affiché est relu après chaque acte.
 */
import React, { useCallback, useEffect, useState } from "react";
import { OhCard, OhSelect, OhStatusNote } from "../../components/ui/oh-charte";
import { formatDateTimeFR } from "../../utils/dateFormat";
import { etatProspection, leverOpposition, poserOpposition } from "./prospectionApi";

const SOURCES = {
  fiche_client: "enregistrée depuis la fiche",
  telephone: "demande par téléphone",
  courrier: "demande par courrier",
  lien_desinscription: "lien de désinscription",
  reponse_stop_email: "réponse STOP par e-mail",
  reponse_stop_sms: "réponse STOP par SMS",
};
const SAISIES = ["fiche_client", "telephone", "courrier"];
const ACTIONS = { opposition: "Opposition enregistrée", levee: "Opposition levée", envoi_refuse: "Envoi commercial refusé", config: "Mentions mises à jour" };
const CANAUX = { email: "e-mail", sms: "SMS", tous: "e-mail et SMS" };

const bouton = {
  height: 36, padding: "0 var(--oh-space-4)", borderRadius: "var(--oh-radius-control)", fontSize: 14, fontWeight: 500, cursor: "pointer",
  background: "transparent", border: "1px solid var(--oh-accent)", color: "var(--oh-accent-text)",
};
const boutonDiscret = { ...bouton, border: "1px solid var(--oh-border)", color: "var(--oh-text-secondary)" };

// Repère « à vérifier » : la donnée existante est conservée telle quelle, seul son format est signalé (paire info, 2 modes).
export function AVerifier({ testid }) {
  return (
    <span data-testid={testid} title="Le format de cette coordonnée semble incorrect : à vérifier avec le client"
          style={{ background: "var(--oh-info-bg)", color: "var(--oh-info-text)", border: "1px solid var(--oh-info-border)",
                   borderRadius: "var(--oh-radius-control)", padding: "0 var(--oh-space-2)", fontSize: 12, lineHeight: "20px", fontWeight: 500 }}>
      À vérifier
    </span>
  );
}

export default function ProspectionOpposition({ clientId }) {
  const [etat, setEtat] = useState(null);
  const [erreur, setErreur] = useState("");
  const [occupe, setOccupe] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [source, setSource] = useState("fiche_client");

  const charger = useCallback(async () => {
    if (!clientId) return;
    try {
      setEtat(await etatProspection(clientId));
      setErreur("");
    } catch (e) {
      setEtat(null);
      setErreur("L'état de l'opposition n'a pas pu être lu.");
    }
  }, [clientId]);

  useEffect(() => { charger(); }, [charger]);

  const agir = async (fonction) => {
    setOccupe(true);
    try {
      await fonction();
      setConfirmation(null);
      await charger();
    } catch (e) {
      setErreur("L'acte n'a pas été enregistré. Réessayez.");
    } finally {
      setOccupe(false);
    }
  };

  const opp = etat?.opposition_prospection;
  const oppose = opp?.active === true;
  const journal = (etat?.journal || []).slice(0, 5);

  return (
    <OhCard data-testid="prospection-bloc" style={{ padding: "var(--oh-space-4)" }}>
      <h4 style={{ margin: 0, fontWeight: 500, color: "var(--oh-text)" }}>Prospection commerciale</h4>

      {erreur && <OhStatusNote variant="error" data-testid="prospection-erreur" style={{ marginTop: "var(--oh-space-3)" }}>{erreur}</OhStatusNote>}

      {etat && oppose && (
        <OhStatusNote variant="info" title="Opposé à la prospection" data-testid="prospection-oppose" style={{ marginTop: "var(--oh-space-3)" }}>
          Depuis le {formatDateTimeFR(opp.le)} · {SOURCES[opp.source] || opp.source} · {CANAUX[opp.canal] || opp.canal}.
          Ce client ne reçoit plus aucune offre.
        </OhStatusNote>
      )}
      {etat && !oppose && (
        <p data-testid="prospection-libre" style={{ margin: "var(--oh-space-3) 0 0", color: "var(--oh-text-secondary)", fontSize: 14, lineHeight: 1.6 }}>
          {opp?.le ? `Opposition levée le ${formatDateTimeFR(opp.le)}. ` : ""}Ce client peut recevoir nos offres.
        </p>
      )}

      {etat && !confirmation && (
        <div style={{ marginTop: "var(--oh-space-3)" }}>
          {oppose ? (
            <button type="button" style={boutonDiscret} data-testid="prospection-lever" aria-label="Lever l'opposition à la prospection"
                    disabled={occupe} onClick={() => setConfirmation("lever")}>Lever l'opposition</button>
          ) : (
            <button type="button" style={bouton} data-testid="prospection-poser" aria-label="Enregistrer une opposition à la prospection"
                    disabled={occupe} onClick={() => setConfirmation("poser")}>Enregistrer une opposition</button>
          )}
        </div>
      )}

      {etat && confirmation === "poser" && (
        <div data-testid="prospection-confirmer-poser" style={{ marginTop: "var(--oh-space-3)", display: "grid", gap: "var(--oh-space-2)" }}>
          <label htmlFor="prospection-source" style={{ fontSize: 14, color: "var(--oh-text-secondary)" }}>Comment le client a-t-il fait sa demande ?</label>
          <OhSelect id="prospection-source" value={source} onChange={(e) => setSource(e.target.value)} data-testid="prospection-source">
            {SAISIES.map((s) => <option key={s} value={s}>{SOURCES[s]}</option>)}
          </OhSelect>
          <div style={{ display: "flex", gap: "var(--oh-space-2)", flexWrap: "wrap" }}>
            <button type="button" style={bouton} data-testid="prospection-confirmer" disabled={occupe}
                    onClick={() => agir(() => poserOpposition(clientId, source))}>Confirmer l'opposition</button>
            <button type="button" style={boutonDiscret} disabled={occupe} onClick={() => setConfirmation(null)}>Annuler</button>
          </div>
        </div>
      )}

      {etat && confirmation === "lever" && (
        <div data-testid="prospection-confirmer-lever" style={{ marginTop: "var(--oh-space-3)", display: "grid", gap: "var(--oh-space-2)" }}>
          <p style={{ margin: 0, fontSize: 14, color: "var(--oh-text)", lineHeight: 1.6 }}>Le client recevra de nouveau nos offres. Lever l'opposition ?</p>
          <div style={{ display: "flex", gap: "var(--oh-space-2)", flexWrap: "wrap" }}>
            <button type="button" style={bouton} data-testid="prospection-confirmer" disabled={occupe}
                    onClick={() => agir(() => leverOpposition(clientId))}>Confirmer la levée</button>
            <button type="button" style={boutonDiscret} disabled={occupe} onClick={() => setConfirmation(null)}>Annuler</button>
          </div>
        </div>
      )}

      {journal.length > 0 && (
        <ul data-testid="prospection-journal" style={{ listStyle: "none", margin: "var(--oh-space-4) 0 0", padding: 0, display: "grid", gap: "var(--oh-space-1)" }}>
          {journal.map((j, i) => (
            <li key={`${j.at}-${i}`} style={{ fontSize: 13, color: "var(--oh-text-muted)", lineHeight: 1.6 }}>
              {formatDateTimeFR(j.at)} · {ACTIONS[j.action] || j.action}{j.source ? ` · ${SOURCES[j.source] || j.source}` : ""}
            </li>
          ))}
        </ul>
      )}
    </OhCard>
  );
}
